import React, { useState, memo } from 'react';
import { ReactComponent as AddIcon } from 'assets/icons/add-counter-icon.svg';
import { ReactComponent as SubtractIcon } from 'assets/icons/minus-counter-icon.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/gray-arrow-down-icon.svg';
import { formatNumber } from 'utils/functions';
import { UpdateOrderBrandMeal } from 'models/order.model';
import { OrderSingleMealEditType } from 'models/meal.model';
import { BaseModal, Button } from '@cokitchen/cokitchen-components';

type Props = {
  brandMeal: UpdateOrderBrandMeal;
  onDeleteMeal: (id: string) => void;
  onIncrement: (meal: OrderSingleMealEditType) => void;
  onDecrement: (meal: OrderSingleMealEditType) => void;
  onEditMeal?: (meal: OrderSingleMealEditType) => void;
};

const UpdateMealCard = ({
  brandMeal,
  onDeleteMeal,
  onIncrement,
  onDecrement,
  onEditMeal
}: Props) => {
  const [expanded, setExpanded] = useState(true);
  const [mealToDelete, setMealToDelete] = useState<OrderSingleMealEditType | null>(null);

  const handleDeleteMeal = () => {
    if (!mealToDelete) return;
    onDeleteMeal(mealToDelete.id);
    setMealToDelete(null);
  };

  const calculateMealTotal = (meal: OrderSingleMealEditType): number => {
    const addonsTotal = (meal.addons || []).reduce(
      (acc, addon) => acc + Number(addon.unit_amount) * addon.quantity * meal.quantity,
      0
    );
    return Number(meal.amount) * meal.quantity + addonsTotal;
  };

  return (
    <div className='p-2 bg-white'>
      <div className='flex items-center justify-between  border-b border-[#E2E7EE]'>
        <h3 className='font-bold py-3 text-sm text-[#858585]'>{brandMeal.brandName}</h3>
        <div>
          <button
            className='bg-[#F7F7F7] p-2 rounded-full'
            onClick={() => setExpanded((prev) => !prev)}>
            <ArrowDownIcon
              className={`transition-transform duration-200 ${
                expanded ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
        </div>
      </div>

      {expanded && (
        <div className='flex justify-between items-start py-5 gap-4'>
          <ul className='flex flex-col gap-2 w-full'>
            {brandMeal.meals.map((meal) => (
              <li
                key={meal.id}
                className='text-sm text-[#1A1A1A] flex gap-2 items-start justify-between'>
                <div className='flex gap-2 items-start'>
                  <div className='flex gap-2 items-center'>
                    <SubtractIcon
                      className='cursor-pointer'
                      onClick={() => onDecrement(meal)}
                      width={18}
                    />
                    <p>{meal.quantity}</p>
                    <AddIcon
                      className='cursor-pointer'
                      onClick={() => onIncrement(meal)}
                      width={18}
                    />
                  </div>
                  <div className='flex flex-col'>
                    <p>
                      <span>{meal.name} </span>
                      <span className='font-medium'>
                        ({formatNumber(calculateMealTotal(meal))})
                      </span>
                    </p>
                    {meal.addons.map((addon) => (
                      <h2 key={addon.id} className='text-xs text-[#858585]'>
                        {addon.quantity}x {addon.name}
                      </h2>
                    ))}
                  </div>
                </div>

                <div className='flex gap-2 items-center'>
                  <EditIcon
                    width={14}
                    className='cursor-pointer'
                    onClick={() => onEditMeal?.(meal)}
                  />
                  <DeleteIcon
                    width={14}
                    className='cursor-pointer'
                    onClick={() => {
                      setMealToDelete(meal);
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {mealToDelete && (
        <BaseModal
          isOpen={mealToDelete != null}
          title=''
          onRequestClose={() => {
            setMealToDelete(null);
          }}
          footer={
            <div className='flex gap-4 items-center'>
              <Button
                onClick={() => {
                  setMealToDelete(null);
                }}
                color='gray'
                variant='block'>
                Cancel
              </Button>
              <Button onClick={handleDeleteMeal} color='primary' variant='block'>
                Delete
              </Button>
            </div>
          }>
          <div className='flex flex-col items-center pt-6'>
            <p className='text-lg'>
              Are you sure you want to delete <strong>{mealToDelete.name}</strong>?
            </p>
          </div>
        </BaseModal>
      )}
    </div>
  );
};

export default memo(UpdateMealCard);
